<template>
  <Transition
    @before-enter="beforeEnter"
    @enter="enter"
    @after-enter="reset"
    @enter-cancelled="cancel"
    @leave="leave"
    @after-leave="reset"
    @leave-cancelled="cancel"
  >
    <slot />
  </Transition>
</template>

<script lang="ts" setup>
import * as animate from '@/util/animate'

const MINIMUM_DURATION_FRACTION = 0.9

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    duration?: number
  }>(),
  {
    disabled: false,
    duration: 500,
  },
)

function cancel(element: Element) {
  if (!(element instanceof HTMLElement)) return

  Object.assign(element.style, {
    width: '',
  })
}

function beforeEnter(element: Element) {
  if (!(element instanceof HTMLElement)) return
  if (props.disabled) return

  element.style.opacity = '0'
}

function enter(element: Element, done: () => void) {
  if (props.disabled || !(element instanceof HTMLElement)) {
    done()
    return
  }

  // Clamp the animated content to avoid animation outside the viewport
  const rect = element.getBoundingClientRect()
  const availableWidth = window.innerWidth - rect.left

  // Element is outside viewport, don't animate
  if (availableWidth < 0 || rect.right < 0) {
    done()
    return
  }

  const effectiveWidth = Math.min(availableWidth, rect.width)
  const effectiveWidthFraction = effectiveWidth / window.innerWidth

  const duration = Math.max(
    MINIMUM_DURATION_FRACTION * props.duration,
    effectiveWidthFraction * props.duration,
  )
  let effectiveDuration = duration
  if (effectiveWidth < rect.width) {
    const hiddenWidth = rect.width - effectiveWidth
    const hiddenWidthFraction = hiddenWidth / effectiveWidth
    effectiveDuration *= 1 + hiddenWidthFraction
  }

  Object.assign(element.style, {
    overflow: 'hidden',
  })

  if (effectiveDuration > duration) {
    Object.assign(element.style, {
      opacity: '',
      width: `${effectiveWidth}px`,
    })

    animate.from(
      element,
      {
        opacity: 0,
        width: 0,
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
      },
      {
        duration: duration,
        onComplete() {
          done()
        },
      },
    )
  } else {
    Object.assign(element.style, {
      opacity: '',
    })

    animate.from(
      element,
      {
        opacity: 0,
        width: 0,
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
      },
      {
        duration,
        onComplete() {
          done()
        },
      },
    )
  }
}

function leave(element: Element, done: () => void) {
  if (props.disabled || !(element instanceof HTMLElement)) {
    done()
    return
  }

  // Clamp the animated content to avoid animation outside the viewport
  const rect = element.getBoundingClientRect()
  const availableWidth = window.innerWidth - rect.left
  const effectiveWidth = Math.min(availableWidth, rect.width)
  const effectiveWidthFraction = effectiveWidth / window.innerWidth

  const duration = Math.max(
    MINIMUM_DURATION_FRACTION * props.duration,
    effectiveWidthFraction * props.duration,
  )

  Object.assign(element.style, {
    overflow: 'hidden',
  })

  if (effectiveWidth < rect.width) {
    Object.assign(element.style, {
      width: `${effectiveWidth}px`,
    })
  }

  animate.to(
    element,
    {
      width: 0,
      opacity: 0,
      marginTop: 0,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
    {
      duration,
      onComplete() {
        done()
      },
    },
  )
}

function reset(element: Element) {
  if (props.disabled) return
  if (!(element instanceof HTMLElement)) return

  Object.assign(element.style, {
    width: '',
    opacity: '',
    marginTop: '',
    marginBottom: '',
    paddingTop: '',
    paddingBottom: '',
    overflow: '',
  })
}
</script>
